import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import type { AuditLog } from "@/hooks/useAdminAuditLogs";

interface AuditLogDetailsDialogProps {
  log: AuditLog | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AuditLogDetailsDialog({
  log,
  open,
  onOpenChange,
}: AuditLogDetailsDialogProps) {
  if (!log) return null;

  const changes = log.details?.changes as Record<string, unknown> | undefined;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Action Details
            <Badge variant={log.action === "delete" ? "destructive" : "secondary"}>
              {log.action}
            </Badge>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          <div className="grid grid-cols-2 gap-2">
            <div className="text-muted-foreground">Date</div>
            <div>{format(new Date(log.created_at), "PPp")}</div>

            <div className="text-muted-foreground">Admin</div>
            <div>
              {log.admin_name || "Unknown"}
              {log.admin_email && (
                <span className="text-muted-foreground text-xs block">
                  {log.admin_email}
                </span>
              )}
            </div>

            <div className="text-muted-foreground">Target User</div>
            <div>
              {log.target_name || "Deleted User"}
              {log.target_email && (
                <span className="text-muted-foreground text-xs block">
                  {log.target_email}
                </span>
              )}
            </div>
          </div>

          {log.action === "update" && changes && (
            <div className="border-t pt-4">
              <h4 className="font-medium mb-2">Changes Made</h4>
              <div className="space-y-1 bg-muted/50 rounded-md p-3">
                {Object.entries(changes).map(([key, value]) => {
                  const isFromTo = typeof value === "object" && value !== null && "from" in value && "to" in value;
                  const fromTo = value as { from: unknown; to: unknown };
                  
                  // Skip if no actual change
                  if (isFromTo && fromTo.from === fromTo.to) return null;
                  
                  const formatVal = (v: unknown) => {
                    if (v === null || v === undefined) return "—";
                    if (v === true) return "Yes";
                    if (v === false) return "No";
                    return String(v);
                  };
                  
                  return (
                    <div key={key} className="flex justify-between gap-2">
                      <span className="text-muted-foreground">
                        {key.replace(/_/g, " ")}
                      </span>
                      <span className="font-mono text-xs text-right">
                        {isFromTo ? (
                          <>
                            <span className="text-muted-foreground">{formatVal(fromTo.from)}</span>
                            {" → "}
                            <span className="text-foreground">{formatVal(fromTo.to)}</span>
                          </>
                        ) : (
                          formatVal(value)
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {log.action === "delete" && (
            <div className="border-t pt-4">
              <h4 className="font-medium mb-2">Deleted User Info</h4>
              {log.details?.deleted_user ? (
                <div className="space-y-1 bg-destructive/10 rounded-md p-3">
                  {(() => {
                    const user = log.details.deleted_user as Record<string, unknown>;
                    const formatVal = (v: unknown) => {
                      if (v === null || v === undefined) return "—";
                      if (v === true) return "Yes";
                      if (v === false) return "No";
                      // Format dates
                      if (typeof v === "string" && v.match(/^\d{4}-\d{2}-\d{2}T/)) {
                        return format(new Date(v), "PPp");
                      }
                      return String(v);
                    };
                    
                    const fields = [
                      { key: "name", label: "Name" },
                      { key: "email", label: "Email" },
                      { key: "phone", label: "Phone" },
                      { key: "member_type", label: "Member Type" },
                      { key: "referral_code", label: "Referral Code" },
                      { key: "credits_balance", label: "Credits" },
                      { key: "is_unlimited", label: "Unlimited" },
                      { key: "unlimited_expires_at", label: "Unlimited Expires" },
                      { key: "was_admin", label: "Was Admin" },
                      { key: "joined_at", label: "Joined At" },
                    ];
                    
                    return fields.map(({ key, label }) => (
                      <div key={key} className="flex justify-between gap-2 text-sm">
                        <span className="text-muted-foreground">{label}</span>
                        <span className="font-mono text-xs text-right">
                          {formatVal(user[key])}
                        </span>
                      </div>
                    ));
                  })()}
                </div>
              ) : (
                <div className="bg-destructive/10 text-destructive rounded-md p-3 text-xs">
                  {log.details?.message as string || "User was permanently deleted"}
                </div>
              )}
              {log.details?.deleted_at && (
                <div className="mt-2 text-xs text-muted-foreground">
                  Deleted at: {format(new Date(log.details.deleted_at as string), "PPp")}
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
